import React from 'react';
import {Box, styled} from '@mui/material';
import CircleLoader from '@/components/circle-loader';
import PoppaysLoader from '@/components/poppays-loader';
import ElementInView from '@/components/element-in-view/ElementInView';
import {media} from '@/styles/mediaQueries';
import Conditional from '@/components/conditional';

// Styled components
const StyledGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  marginBottom: '48px',
  [media.mobile]: {
    'flexDirection': 'column',
    'alignItems': 'stretch',
    '& > *': {
      display: 'flex',
      width: '100%',
    },
  },
}));

const StyledLoaderContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
}));

// Props interface for the component
export interface InfiniteGridProps {
  children: React.ReactNode;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isLoadingNextPage?: boolean;
  onLoadMore: () => Promise<void>;
  className?: string;
  columnGap?: string;
  rowGap?: string;
}

/**
 * A reusable infinite scrolling grid component.
 * Renders a grid of children and automatically loads more when the user scrolls to the bottom.
 */
function InfiniteGrid({
  children,
  isLoading = false,
  hasNextPage = false,
  isLoadingNextPage = false,
  onLoadMore,
  className,
  columnGap,
  rowGap,
}: InfiniteGridProps) {
  // Apply custom gap if provided
  const gridStyle = {
    columnGap: columnGap || '20px',
    rowGap: rowGap || '20px',
  };

  return (
    <>
      <StyledGrid className={className} sx={gridStyle}>
        {children}
      </StyledGrid>

      <Conditional condition={isLoading || isLoadingNextPage}>
        <StyledLoaderContainer>
          <CircleLoader size="medium" />
        </StyledLoaderContainer>
      </Conditional>

      <Conditional condition={hasNextPage}>
        <ElementInView onInView={onLoadMore} height={100} />
      </Conditional>
    </>
  );
}

export default InfiniteGrid;
